import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTenant } from '../context/TenantContext';

const MESES_ABREV = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];

function PlanoAnual({ instrumentos, nomeEmpresa }) {
  const hoje = new Date();
  const hojeAno = hoje.getFullYear();
  const hojeMs = hoje.getMonth();

  const meses = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const totalMs = hojeMs + i;
      const m = totalMs % 12;
      const a = hojeAno + Math.floor(totalMs / 12);
      const isPast = a < hojeAno || (a === hojeAno && m < hojeMs);
      const isCurrent = m === hojeMs && a === hojeAno;
      const isProximo = !isPast && !isCurrent && i <= 1;

      const insts = instrumentos.filter(inst => {
        if (!inst.nextCalibrationDate) return false;
        const d = new Date(inst.nextCalibrationDate);
        return d.getMonth() === m && d.getFullYear() === a;
      });

      let cor = 'bg-emerald-500';
      let corTexto = 'text-emerald-100';
      if (isPast) { cor = 'bg-red-500'; corTexto = 'text-red-100'; }
      else if (isCurrent || isProximo) { cor = 'bg-amber-500'; corTexto = 'text-amber-100'; }

      return { m, a, label: `${MESES_ABREV[m]}/${String(a).slice(2)}`, count: insts.length, cor, corTexto, isCurrent };
    });
  }, [instrumentos, hojeMs, hojeAno]);

  return (
    <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-[var(--surface-4)] flex items-center justify-between">
        <h3 className="text-base font-semibold text-white">Plano Anual de Calibrações</h3>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">próximos 12 meses</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead>
            <tr className="border-b border-[var(--surface-4)]">
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-36">
                PLANO GLOBAL
              </th>
              {meses.map(({ label, isCurrent }) => (
                <th key={label} className={`px-3 py-2.5 text-[10px] font-semibold tracking-wider text-center min-w-[72px] ${
                  isCurrent ? 'text-blue-400' : 'text-slate-500'
                }`}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-5 py-4 text-sm text-slate-300 font-medium bg-[var(--surface-2)] border-r border-[var(--surface-4)]">
                {nomeEmpresa}
              </td>
              {meses.map(({ label, count, cor, corTexto, isCurrent }) => (
                <td key={label} className={`px-3 py-4 text-center ${isCurrent ? 'bg-blue-950/10' : ''}`}>
                  {count > 0 ? (
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${cor} ${corTexto}`}>
                      {count}
                    </span>
                  ) : (
                    <span className="text-slate-700 text-xs">—</span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-[var(--surface-4)] flex items-center gap-5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Em dia</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Atenção</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Vencido</span>
      </div>
    </div>
  );
}

const CARDS = [
  {
    chave: 'ok',
    rotulo: 'Em dia',
    descricao: 'instrumentos calibrados',
    corNumero: 'text-emerald-400',
    corBorda: 'border-emerald-900/40',
    corFundo: 'bg-emerald-950/10',
    corHover: 'hover:border-emerald-700/60 hover:bg-emerald-950/20',
    filtro: 'calibrado',
    icone: '✅',
  },
  {
    chave: 'attention',
    rotulo: 'Atenção',
    descricao: 'a vencer em breve',
    corNumero: 'text-amber-400',
    corBorda: 'border-amber-900/40',
    corFundo: 'bg-amber-950/10',
    corHover: 'hover:border-amber-700/60 hover:bg-amber-950/20',
    filtro: 'a_vencer',
    icone: '⚠️',
  },
  {
    chave: 'overdue',
    rotulo: 'Vencidos',
    descricao: 'instrumentos vencidos',
    corNumero: 'text-red-400',
    corBorda: 'border-red-900/40',
    corFundo: 'bg-red-950/10',
    corHover: 'hover:border-red-700/60 hover:bg-red-950/20',
    filtro: 'vencido',
    icone: '🔴',
  },
  {
    chave: 'blocked',
    rotulo: 'Bloqueados',
    descricao: 'aguardando nova calibração',
    corNumero: 'text-slate-300',
    corBorda: 'border-slate-700/40',
    corFundo: 'bg-slate-800/20',
    corHover: 'hover:border-slate-600/60 hover:bg-slate-800/40',
    filtro: 'bloqueado',
    icone: '🔒',
  },
  {
    chave: 'inCalibration',
    rotulo: 'Em calibração',
    descricao: 'calibração externa + aguardando laudo',
    corNumero: 'text-violet-400',
    corBorda: 'border-violet-900/40',
    corFundo: 'bg-violet-950/10',
    corHover: 'hover:border-violet-700/60 hover:bg-violet-950/20',
    filtro: 'em_calibracao',
    icone: '🔄',
  },
];

function dayClass(days) {
  if (days <= 7) return 'text-red-400 font-semibold';
  if (days <= 14) return 'text-amber-400 font-semibold';
  return 'text-yellow-400';
}

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [instrumentosPlano, setInstrumentosPlano] = useState([]);
  const { tenant } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    carregarSummary();
    api.get('/instruments?calibratable=true&limit=1000')
      .then(r => setInstrumentosPlano(r.data.dados || r.data || []))
      .catch(() => setInstrumentosPlano([]));
  }, []);

  const carregarSummary = async () => {
    setCarregando(true);
    try {
      const res = await api.get('/dashboard/summary');
      setSummary(res.data);
      setErro(false);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      setErro(true);
    } finally {
      setCarregando(false);
    }
  };

  function irParaInstrumentos(filtro) {
    navigate(`/instruments?status=${filtro}`);
  }

  if (erro) {
    return (
      <div className="p-6 text-center text-slate-400">
        Não foi possível carregar o dashboard. Tente novamente.
      </div>
    );
  }

  const data = summary || { ok: '—', attention: '—', overdue: '—', blocked: '—', inCalibration: '—', alerts: [], proximas: [], porSetor: [] };

  // Compliance rate: calibrado / (calibratable total = all calibratable statuses)
  const totalCalibratable = CARDS.reduce((acc, c) => acc + (Number(data[c.chave]) || 0), 0);
  const complianceRate = totalCalibratable > 0 ? Math.round(((Number(data.ok) || 0) / totalCalibratable) * 100) : null;

  return (
    <div className="p-8 space-y-8">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Visão Geral</h1>
          <p className="text-sm text-slate-500 mt-1">
            {carregando ? 'Carregando...' : `${data.total ?? '—'} instrumentos cadastrados`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {!carregando && complianceRate !== null && (
            <div className="text-right">
              <div className={`text-2xl font-bold tabular-nums ${complianceRate >= 80 ? 'text-emerald-400' : complianceRate >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                {complianceRate}%
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">conformidade</div>
            </div>
          )}
          <button
            onClick={carregarSummary}
            className="text-xs text-slate-500 hover:text-slate-300 border border-[var(--surface-4)] hover:border-slate-600 px-3 py-1.5 rounded-lg transition-colors"
          >
            ↻ Atualizar
          </button>
        </div>
      </div>

      {/* ── Cards de status ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {CARDS.map((card) => (
          <button
            key={card.chave}
            onClick={() => irParaInstrumentos(card.filtro)}
            className={`text-left border rounded-xl p-6 transition-all duration-200 cursor-pointer group ${card.corBorda} ${card.corFundo} ${card.corHover}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                {card.rotulo}
              </div>
              <span className="text-base opacity-70 group-hover:opacity-100 transition-opacity">
                {card.icone}
              </span>
            </div>
            {carregando ? (
              <div className="h-10 w-16 bg-[var(--surface-4)] rounded animate-pulse mb-2" />
            ) : (
              <div className={`text-4xl font-bold tabular-nums ${card.corNumero}`}>
                {data[card.chave] ?? '—'}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-1">{card.descricao}</div>
            <div className="mt-4 text-xs text-slate-600 group-hover:text-slate-400 transition-colors">
              Ver instrumentos →
            </div>
          </button>
        ))}
      </div>

      {/* ── Segunda linha: Distribuição + Próximas calibrações + Alertas ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Distribuição por status */}
        <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white mb-5">Distribuição por Status</h3>
          {carregando ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-8 bg-[var(--surface-4)] rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {CARDS.map((card) => {
                const valor = Number(data[card.chave]) || 0;
                const pct = totalCalibratable > 0 ? Math.round((valor / totalCalibratable) * 100) : 0;
                return (
                  <button
                    key={card.chave}
                    onClick={() => irParaInstrumentos(card.filtro)}
                    className="w-full text-left group"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-400">{card.icone} {card.rotulo}</span>
                      <span className={`text-xs font-semibold ${card.corNumero}`}>
                        {valor} <span className="text-slate-600 font-normal">({pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          card.chave === 'ok' ? 'bg-emerald-500' :
                          card.chave === 'attention' ? 'bg-amber-500' :
                          card.chave === 'overdue' ? 'bg-red-500' :
                          card.chave === 'inCalibration' ? 'bg-violet-500' :
                          'bg-slate-400'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Próximas calibrações (30 dias) */}
        <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-white">Próximas Calibrações</h3>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">próximos 30 dias</span>
          </div>
          {carregando ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 bg-[var(--surface-4)] rounded animate-pulse" />
              ))}
            </div>
          ) : !data.proximas || data.proximas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
              <span className="text-3xl mb-2">✅</span>
              <p className="text-sm">Nenhuma calibração vencendo nos próximos 30 dias</p>
            </div>
          ) : (
            <div className="space-y-1">
              {data.proximas.map((p) => (
                <button
                  key={p.id}
                  onClick={() => irParaInstrumentos('a_vencer')}
                  className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-[var(--surface-4)] transition-colors group text-left"
                >
                  <span className="font-mono text-xs text-slate-300 w-20 flex-shrink-0">{p.tag}</span>
                  <span className="text-[11px] text-slate-500 flex-1 truncate">{p.description}</span>
                  <span className="text-[10px] text-slate-600 flex-shrink-0">{p.sector}</span>
                  <span className={`text-[11px] flex-shrink-0 tabular-nums ${dayClass(p.daysRemaining)}`}>
                    {p.daysRemaining === 0 ? 'hoje' : `${p.daysRemaining}d`}
                  </span>
                </button>
              ))}
              {(data.attention || 0) > (data.proximas?.length || 0) && (
                <button
                  onClick={() => irParaInstrumentos('a_vencer')}
                  className="w-full text-center text-[10px] text-blue-400 hover:text-blue-300 pt-2 transition-colors"
                >
                  Ver todos os {data.attention} instrumentos a vencer →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Alertas críticos */}
        <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white mb-5">Alertas Críticos</h3>
          {carregando ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-[var(--surface-4)] rounded animate-pulse" />
              ))}
            </div>
          ) : data.alerts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
              <span className="text-3xl mb-2">✅</span>
              <p className="text-sm">Nenhum alerta crítico no momento.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.alerts.map((alert, idx) => (
                <button
                  key={idx}
                  onClick={() => irParaInstrumentos(alert.type)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--surface-4)] transition-colors group"
                >
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    alert.type === 'vencido' ? 'bg-red-500' :
                    alert.type === 'bloqueado' ? 'bg-slate-400' : 'bg-amber-500'
                  }`} />
                  <span className="text-sm text-slate-300 flex-1">{alert.message}</span>
                  <span className="text-slate-600 group-hover:text-slate-400 text-xs">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Plano Anual ──────────────────────────────────────────────────── */}
      <PlanoAnual instrumentos={instrumentosPlano} nomeEmpresa={tenant?.nomeEmpresa || tenant?.name || 'Empresa'} />

      {/* ── Terceira linha: Conformidade por Setor ───────────────────────── */}
      <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6">
        <h3 className="text-base font-semibold text-white mb-5">Conformidade por Setor</h3>
        {carregando ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-[var(--surface-4)] rounded animate-pulse" />
            ))}
          </div>
        ) : !data.porSetor || data.porSetor.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <p className="text-sm">Nenhum dado de setor disponível</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {data.porSetor.map((s) => (
              <button
                key={s.sector}
                onClick={() => navigate(`/instruments?sector=${encodeURIComponent(s.sector)}`)}
                className="w-full text-left group"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-300 truncate max-w-[60%]">{s.sector}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-600">{s.ok}/{s.total}</span>
                    <span className={`text-xs font-semibold tabular-nums w-10 text-right ${
                      s.pct >= 80 ? 'text-emerald-400' : s.pct >= 50 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {s.pct}%
                    </span>
                  </div>
                </div>
                <div className="h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      s.pct >= 80 ? 'bg-emerald-500' : s.pct >= 50 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${s.pct}%` }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Atalhos rápidos ───────────────────────────────────────────────── */}
      <div className="border-t border-[var(--surface-4)] pt-6">
        <p className="text-xs text-slate-600 uppercase tracking-wider mb-3">Ações rápidas</p>
        <div className="flex flex-wrap gap-3">
          {[
            { rotulo: '🔴 Ver vencidos', filtro: 'vencido' },
            { rotulo: '⚠️ Ver a vencer', filtro: 'a_vencer' },
            { rotulo: '🔒 Ver bloqueados', filtro: 'bloqueado' },
            { rotulo: '✅ Ver em dia', filtro: 'calibrado' },
          ].map((acao) => (
            <button
              key={acao.filtro}
              onClick={() => irParaInstrumentos(acao.filtro)}
              className="text-xs px-4 py-2 bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-400 rounded-lg hover:border-slate-500 hover:text-slate-200 transition-colors"
            >
              {acao.rotulo}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
